import { Accordion, Card } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import socket from "../../../Utils/socketClient";
import Swal from "sweetalert2";
import { getUserFromToken } from "../../../Utils/TokenVerify";
import { AddCondition } from "../../../Services/Superadmin/Superadmin";
import { commandAlert } from "../../../Utils/Commanalert";

const AggregatedPosition = ({ groupedData, search = "" }) => {
  const [cardPage, setCardPage] = useState(0);
  const [pageStates, setPageStates] = useState({});
  const [livePrices, setLivePrices] = useState({});
  const [prevPrices, setPrevPrices] = useState({});
  const [tablePageSize, setTablePageSize] = useState(10);

  const TokenData = getUserFromToken();
  const user_id = TokenData?.user_id;
  // console.log("livePrices",livePrices)
  const cardPageSize = 5;
  // const tablePageSize = 10;

  useEffect(() => {
    const handleData = (tick) => {
      if (!tick?.tk || !tick?.lp) return;
      const token = String(tick.tk);
      const price = Number(tick.lp);
      if (!isNaN(price) && price > 0) {
        setLivePrices((prev) => {
          const currentPrice = prev[token];
          if (currentPrice !== price?.toFixed(4)) {
            setPrevPrices((p) => ({ ...p, [token]: currentPrice }));
          }
          return {
            ...prev,
            [token]: price?.toFixed(4),
          };
        });
      }
    };

    socket.on("alice_ticks", handleData);
    return () => socket.off("alice_ticks", handleData);
  }, []);

  const filteredGroups = React.useMemo(() => {
    if (!search?.trim()) return groupedData;
    const lower = search.trim().toLowerCase();
    return groupedData.filter((group) => {
      const symbol = group._id?.symbol?.toLowerCase() || "";
      if (symbol.includes(lower)) return true;
      if (
        group.records?.some((r) =>
          (r.userName || "").toLowerCase().includes(lower)
        )
      )
        return true;
      return false;
    });
  }, [groupedData, search]);

  const totalCardPages = Math.ceil(filteredGroups.length / cardPageSize);
  const displayedGroups = filteredGroups.slice(
    cardPage * cardPageSize,
    (cardPage + 1) * cardPageSize
  );

  const SetConditions = async (type, symbol, livePrice, avgPrice) => {
    const { value: inputDrop } = await Swal.fire({
      title: "Enter Drop Threshold",
      input: "number",
      inputLabel: "Enter the drop amount (e.g., 1000 for $1000)",
      inputPlaceholder: "e.g. 1000",
      inputAttributes: {
        min: 1,
        step: 1,
      },
      showCancelButton: true,
      customClass: { popup: "swal2-trading-popup" },
      background: "#f8fafc",
      color: "#1e293b",
    });

    if (!inputDrop) {
      commandAlert({
        type: "info",
        title: "Cancelled",
        text: "You cancelled the condition setup",
        icon: "warn",
      });
      return;
    }

    const dropValue = parseFloat(inputDrop);
    if (isNaN(dropValue)) {
      commandAlert({
        type: "error",
        title: "Error",
        text: "Invalid drop amount",
        icon: "error",
      });
      return;
    }

    const dropThreshold = type === "up" ? dropValue : -Math.abs(dropValue);

    commandAlert({
      type: "info",
      title: "Setting Condition",
      text: `Setting ${type} condition for ${symbol} at price ${livePrice} with threshold ${dropThreshold}`,
      icon: "trade",
      showLoading: true,
    });

    const data = {
      userId: user_id,
      symbol: symbol,
      initialPrice: livePrice,
      dropThreshold: type === "up" ? dropThreshold : -Math.abs(dropThreshold),
      timeWindow: 60,
    };

    try {
      const response = await AddCondition(data);

      if (response.status) {
        commandAlert({
          type: "success",
          title: "Success",
          text: "Condition set successfully!",
          icon: "success",
        });
      } else {
        commandAlert({
          type: "error",
          title: "Error",
          text: response.error || "Failed to set condition",
          icon: "error",
        });
      }
    } catch (error) {
      commandAlert({
        type: "error",
        title: "Error",
        text: error.message || "Network error",
        icon: "error",
      });
    }
  };

  function formatDecimal(value) {
    if (value == null) return "0";
    const strVal = value.toString();
    if (strVal.includes(".")) {
      const [intPart, decimalPart] = strVal.split(".");
      if (decimalPart.length > 4) {
        return parseFloat(value)?.toFixed(2);
      } else {
        return strVal;
      }
    }
    return strVal;
  }

  const getLivePrice = (token, fallbackPrice) => {
    const tokenKey = String(token);
    return livePrices[tokenKey] || fallbackPrice || 0;
  };

  const calculateRecordPL = (item, currentLivePrice) => {
    if (item.signal_type === "buy_sell") {
      return (currentLivePrice - (item.buy_price || 0)) * (item.buy_qty || item.buy_lot || 0);
    } else if (item.signal_type === "sell_buy") {
      return ((item.sell_price || 0) - currentLivePrice) * (item.sell_qty || item.sell_lot || 0);
    }
    return 0;
  };

  return (
    <div className="container-fluid px-3">
      {displayedGroups.map((group, index) => {
        const actualIndex = cardPage * cardPageSize + index;
        const { _id: { symbol, signal_type }, token, count, avg_buy_price, avg_sell_price, total_buy_lot, total_sell_lot, avg_stoploss_price, avg_target_price, live_prices, records } = group;


        const signalColor =
          signal_type === "buy_sell" ? "#198754" :
            signal_type === "sell_buy" ? "#dc3545" : "#0d6efd";

        const currentPage = pageStates[actualIndex] || 0;
        const totalPages = Math.ceil(records?.length / tablePageSize);
        const tokenKey = String(token);
        const livePrice = getLivePrice(tokenKey, live_prices?.Mid_Price);

        const prevPrice = prevPrices[tokenKey];

        let priceColor = "#0d6efd";
        if (prevPrice && livePrice) {
          const current = parseFloat(livePrice);
          const previous = parseFloat(prevPrice);
          if (current > previous) priceColor = "green";
          else if (current < previous) priceColor = "red";
        }

        const buyPL = records?.reduce((acc, item) => {
          if (item.signal_type === "buy_sell") {
            const currentPrice = getLivePrice(item.token, live_prices?.Mid_Price);
            acc += calculateRecordPL(item, currentPrice);
          }
          return acc;
        }, 0) || 0;

        const sellPL = records?.reduce((acc, item) => {
          if (item.signal_type === "sell_buy") {
            const currentPrice = getLivePrice(item.token, live_prices?.Mid_Price);
            acc += calculateRecordPL(item, currentPrice);
          }
          return acc;
        }, 0) || 0;

        const overallPL = buyPL + sellPL;

        return (
          <Card className="mb-4 shadow animated-card border-0" key={actualIndex}>
            <Card.Header className="custom-header py-3 px-4 agp-header d-block">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 agp-header-row d-block">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center flex-wrap mb-2 agp-header-symbol-row">
                    <h4 className="mb-0 fw-bold me-3 header-symbol agp-symbol-title">
                      <span>{symbol}</span>
                      <span style={{ color: signalColor, fontWeight: "bold", marginLeft: "0.5rem" }} />
                    </h4>
                    <span>Count: {count}</span>
                  </div>

                  <div className="averages-text mt-2">
                    <span className="text-secondary me-2 agp-avg-label">🎯 Averages:</span>
                    <div className="d-flex flex-wrap gap-3 averages-row mt-1">
                      <span className="agp-avg agp-avg-buy">Buy Price: <strong>{avg_buy_price?.toFixed(2) ?? "-"}</strong></span>
                      <span className="agp-avg agp-avg-sell">Sell Price: <strong>{avg_sell_price?.toFixed(2) ?? "-"}</strong></span>
                      <span className="agp-avg agp-avg-lot">Buy Lot: <strong>{formatDecimal(total_buy_lot)}</strong></span>
                      <span className="agp-avg agp-avg-lot">Sell Lot: <strong>{formatDecimal(total_sell_lot)}</strong></span>
                      <span className="agp-avg agp-avg-lot">Sell Lot: <strong>{formatDecimal(total_sell_lot)}</strong></span>
                      <span className="agp-avg agp-avg-lot">Target: <strong>{formatDecimal(avg_target_price)}</strong></span>
                      <span className="agp-avg agp-avg-lot">Stop Loss: <strong>{formatDecimal(avg_stoploss_price)}</strong></span>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-row gap-2 agp-btn-row">
                  <button
                    className="btn btn-sm btn-outline-success animate-btn agp-btn agp-btn-up"
                    onClick={() => SetConditions("up", symbol, livePrice, avg_buy_price || avg_sell_price)}
                  >🔼 Up Side</button>
                  <button
                    className="btn btn-sm btn-outline-danger animate-btn agp-btn agp-btn-down"
                    onClick={() => SetConditions("down", symbol, livePrice, avg_buy_price || avg_sell_price)}
                  >🔽 Down Side</button>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-6 fw-semibold agp-live-label"><strong>Live Price:</strong></span>
                  <span className={`agp-live-price ${priceColor === "green" ? "agp-live-green" : priceColor === "red" ? "agp-live-red" : ""}`}>
                    {formatDecimal(livePrice)}
                  </span>
                </div>

                <div>
                  <p className="d-flex align-items-center gap-2" style={{ fontWeight: "bold", margin: 0 }}>
                    Buy Profit/Loss: <span style={{ color: buyPL >= 0 ? "green" : "red" }}>{buyPL.toFixed(2)}</span>
                    Sell Profit/Loss: <span style={{ color: sellPL >= 0 ? "green" : "red" }}>{sellPL.toFixed(2)}</span>
                  </p>
                  <p className="d-flex align-items-end gap-4 justify-content-end" style={{ fontWeight: "bold", margin: 0 }}>
                    Overall Profit/Loss: <span style={{ color: overallPL >= 0 ? "green" : "red" }}>{overallPL.toFixed(2)}</span>
                  </p>
                </div>
              </div>
            </Card.Header>

            <Accordion defaultActiveKey={null}>
              <Accordion.Item eventKey="0">
                <Accordion.Header>📂 View All Records</Accordion.Header>
                <Accordion.Body>
                  <div className="table-responsive animated-table" style={{ animation: "fadeInUp 0.5s cubic-bezier(0.23, 1, 0.32, 1)" }}>
                    <table className="table table-striped table-bordered table-sm align-middle text-center mb-0" style={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)", background: "#fff" }}>
                      <thead style={{ position: "sticky", top: 0, zIndex: 2, background: "#f1f3f6", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                        <tr>
                          <th>#</th>
                          <th>Signal Type</th>
                          {/* <th>Trade Type</th>
                          <th>Converted</th> */}
                          <th>User Name</th>
                          {/* <th>Symbol</th> */}
                          <th>Buy Price</th>
                          <th>Sell Price</th>
                          <th>Live Price</th>
                          {/* <th>P/L</th> */}
                          <th>Buy Qty</th>
                          <th>Sell Qty</th>
                          <th>Target</th>
                          <th>Stop Loss</th>
                          <th>Max Loss %</th>
                          {/* <th>Status</th> */}
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records?.slice(currentPage * tablePageSize, (currentPage + 1) * tablePageSize).map((item, idx) => {
                          const itemLivePrice = getLivePrice(item.token, live_prices?.Mid_Price);
                          const itemPL = calculateRecordPL(item, itemLivePrice);

                          return (
                            <tr key={idx} className="table-row-animate" style={{ transition: "background 0.2s, box-shadow 0.2s", cursor: "pointer" }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafd")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                            >
                              <td>{currentPage * tablePageSize + idx + 1}</td>
                              <td>
                                <span className="badge" style={{ backgroundColor: item.signal_type === "buy_sell" ? "#198754" : "#dc3545" }}>
                                  {item.signal_type === "buy_sell" ? "BUY" : "SELL"}
                                </span>
                              </td>
                              {/* <td>
                                <span className="badge bg-primary">
                                  {item.buy_type ? item.buy_type : item.sell_type ? item.sell_type : "-"}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-info">{item.Converted || "-"}</span>
                              </td> */}
                              <td>{item.userName || "Unknown"}</td>
                              {/* <td><strong>{item.symbol}</strong></td> */}
                              <td>{item.buy_price ? item.buy_price.toFixed(2) : "-"}</td>
                              <td>{item.sell_price ? item.sell_price.toFixed(2) : "-"}</td>
                              <td style={{ color: priceColor, fontWeight: "bold" }}>{formatDecimal(itemLivePrice)}</td>
                              {/* <td style={{ color: itemPL >= 0 ? "green" : "red", fontWeight: "bold" }}>{itemPL.toFixed(2)}</td> */}
                              <td>{item.buy_qty || item.buy_lot || "-"}</td>
                              <td>{item.sell_qty || item.sell_lot || "-"}</td>
                              <td>{item.Target_price ?? "-"}</td>
                              <td>{item.stoploss_price ?? "-"}</td>
                              <td>{item.Sl_price_percentage != null ? parseFloat(item.Sl_price_percentage).toFixed(2) + "%" : "-"}</td>
                              {/* <td>
                                <span className="badge" style={{ backgroundColor: item.status === "Completed" ? "#0d6efd" : "#6c757d" }}>
                                  {item.status || "N/A"}
                                </span>
                              </td> */}
                              <td style={{ fontSize: "0.85rem" }}>{new Date(item.buy_time || item.createdAt).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {/* <div className="d-flex justify-content-end align-items-center mt-3" style={{ animation: "fadeIn 0.3s", gap: "0.5rem" }}>
                    <button className="btn btn-sm btn-outline-primary me-2" disabled={currentPage === 0} onClick={() => setPageStates((prev) => ({ ...prev, [actualIndex]: currentPage - 1 }))}>◀ Prev</button>
                    <span style={{ fontWeight: 500 }}>Page {currentPage + 1} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-primary ms-2" disabled={currentPage + 1 >= totalPages} onClick={() => setPageStates((prev) => ({ ...prev, [actualIndex]: currentPage + 1 }))}>Next ▶</button>
                  </div> */}
                  <div
                    className="d-flex justify-content-between align-items-center mt-3"
                    style={{ animation: "fadeIn 0.3s" }}
                  >
                    {/* Left Side – Rows per page */}
                    <div className="d-flex justify-content-start align-items-center">
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "120px" }}
                        value={tablePageSize}
                        onChange={(e) => {
                          setTablePageSize(Number(e.target.value));
                          setPageStates((prev) => ({
                            ...prev,
                            [actualIndex]: 0,
                          }));
                        }}
                      >
                        <option value={5}>5 </option>
                        <option value={10}>10 </option>
                        <option value={20}>20</option>
                        <option value={50}>50 </option>
                        <option value={100}>100 </option>
                      </select>
                    </div>

                    {/* Right Side – Pagination */}
                    <div className="d-flex justify-content-end align-items-center" style={{ gap: "0.5rem" }}>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        disabled={currentPage === 0}
                        style={{
                          transition: "all 0.2s",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                        onClick={() =>
                          setPageStates((prev) => ({
                            ...prev,
                            [actualIndex]: currentPage - 1,
                          }))
                        }
                      >
                        ◀ Prev
                      </button>

                      <span style={{ fontWeight: 500 }}>
                        Page {currentPage + 1} of {totalPages}
                      </span>

                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        disabled={currentPage + 1 >= totalPages}
                        style={{
                          transition: "all 0.2s",
                          opacity: currentPage + 1 >= totalPages ? 0.5 : 1,
                        }}
                        onClick={() =>
                          setPageStates((prev) => ({
                            ...prev,
                            [actualIndex]: currentPage + 1,
                          }))
                        }
                      >
                        Next ▶
                      </button>
                    </div>
                  </div>

                  <style>
                    {`
                      @keyframes fadeInUp {0% {opacity: 0; transform: translateY(30px);} 100% {opacity: 1; transform: translateY(0);}}
                      @keyframes fadeIn {0% {opacity:0;} 100% {opacity:1;}}
                      .table-row-animate { animation: fadeIn 0.3s; }
                      .agp-live-green { color: green; font-weight: bold; }
                      .agp-live-red { color: red; font-weight: bold; }
                    `}
                  </style>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card>
        );
      })}

      <div className="d-flex justify-content-end align-items-center my-4 w-100">
        <button
          className="btn btn-outline-secondary me-2"
          disabled={cardPage === 0}
          onClick={() => setCardPage((prev) => prev - 1)}
        >
          ◀ Prev
        </button>

        <span className="mx-2">Card Page {cardPage + 1} of {totalCardPages}</span>

        <button
          className="btn btn-outline-secondary ms-2"
          disabled={cardPage + 1 >= totalCardPages}
          onClick={() => setCardPage((prev) => prev + 1)}
        >
          Next ▶
        </button>
      </div>

    </div>
  );
};

export default AggregatedPosition;
